import { Breakpoint, BreakpointV } from "@/types/breakpoints";
import defensiveCopy from "@/util/defensive-copy";
import { strToError, _Error } from "../wordpress/data";
import { Either, fold, left, right } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import { iotsErrorsToCustomErrorType } from "@/util/io-ts-errors-to-_error";
import GlobalModule from "@/store/modules/global";
import { IconName } from "@/lib/icons/icons";
import { IconRotation } from "@/types/icons";

export const BASE_BREAKPOINT_ID = "breakpoint_base";
export const FIRST_RESPONSIVE_BREAKPOINT_ID = "breakpoint_tablet_landscape";

export const DEFAULT_MINIMUM_BREAKPOINT_WIDTH = 250; /* apple watch is 272px */

export const DEFAULT_SECTION_WIDTH = 1120;

export const DEVICE_ICONS: Record<
  string,
  { icon: IconName; rotate?: IconRotation } | undefined
> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  breakpoint_base: { icon: "DesktopIcon" },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  breakpoint_tablet_landscape: { icon: "TabletIcon", rotate: "90" },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  breakpoint_tablet_portrait: { icon: "TabletIcon" },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  breakpoint_phone_landscape: { icon: "MobileIcon", rotate: "90" },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  breakpoint_phone_portrait: { icon: "MobileIcon" }
};

export const customBreakpointIcon = "StarIcon";

// always ensure this function is in sync with the PHP version of mediaQueryString
export function mediaQueryString(
  minWidth: number | undefined | null,
  maxWidth: number | undefined | null
): string | false {
  if (!minWidth && maxWidth) {
    return `@media (max-width: ${maxWidth}px)`;
  } else if (minWidth && !maxWidth) {
    return `@media (min-width: ${minWidth}px)`;
  } else if (minWidth && maxWidth) {
    return `@media (min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`;
  }

  return false;
}

export function validateBreakpoint(
  newBreakpoint: Partial<Breakpoint>
): Either<_Error[], Breakpoint> {
  if (!newBreakpoint.minWidth && !newBreakpoint.maxWidth) {
    return left([strToError("Either minWidth or maxWidth must be set.")]);
  }

  if (!newBreakpoint.label) {
    return left([strToError("A label is required")]);
  }

  if (!newBreakpoint.defaultPreviewWidth) {
    return left([strToError("Please enter a default preview width.")]);
  }

  if (newBreakpoint.defaultPreviewWidth < DEFAULT_MINIMUM_BREAKPOINT_WIDTH) {
    return left([
      strToError(
        `The preview width must be at least ${DEFAULT_MINIMUM_BREAKPOINT_WIDTH}px`
      )
    ]);
  }

  if (newBreakpoint.defaultPreviewWidth !== "100%") {
    if (isNaN(newBreakpoint.defaultPreviewWidth)) {
      return left([strToError("The preview width must be a number or 100%.")]);
    }
  }

  return pipe(
    BreakpointV.decode(newBreakpoint),
    fold(e => {
      return left(iotsErrorsToCustomErrorType(e));
    }, right)
  );
}

export function addOrUpdateBreakpoint(
  breakpoints: Breakpoint[],
  breakpointToAddOrUpdate: Breakpoint
): Breakpoint[] {
  const newBreakpoints = defensiveCopy(breakpoints);
  const index = newBreakpoints.findIndex(
    b => b.id === breakpointToAddOrUpdate.id
  );
  if (index === -1) {
    newBreakpoints.push(breakpointToAddOrUpdate);
  } else {
    newBreakpoints[index] = breakpointToAddOrUpdate;
  }

  return newBreakpoints;
}

export function getMinimumWidth(globalStore: GlobalModule) {
  const sectionWidth = Number(
    globalStore.globalSettings?.settings?.sections?.container_width
  );

  return (sectionWidth || DEFAULT_SECTION_WIDTH) + 12;
}
